import { useEffect, useState } from 'react';
import { useIsOneTrustLoaded } from 'src/containers/ConsentManager/ConsentManager';
import {
  getVisitorCountry,
  isPaidTraffic,
} from 'src/lib/is-us-organic-form-traffic';
import { getCloudFrontViewerCountry } from 'src/lib/visitor-country';
import { contactRoutingConfig } from './config';

export type ExclusionReason = 'country' | 'paid' | 'unknown';

export type Eligibility =
  | { status: 'pending' }
  | { status: 'eligible' }
  | { status: 'excluded'; reason: ExclusionReason };

type Resolved = Exclude<Eligibility, { status: 'pending' }>;

const PENDING: Eligibility = { status: 'pending' };
const UNKNOWN: Resolved = { status: 'excluded', reason: 'unknown' };

const classify = (country: string): Resolved => {
  if (country !== 'US') {
    return { status: 'excluded', reason: 'country' };
  }
  if (isPaidTraffic()) {
    return { status: 'excluded', reason: 'paid' };
  }
  return { status: 'eligible' };
};

const evaluate = (): Resolved | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  const country = getCloudFrontViewerCountry() ?? getVisitorCountry();
  return country === null ? null : classify(country);
};

const isOneTrustPresent = () => {
  return (
    typeof window !== 'undefined' &&
    typeof window.OneTrust?.getGeolocationData === 'function'
  );
};

export const useContactRoutingEligibility = (): Eligibility => {
  const hasOneTrustLoaded = useIsOneTrustLoaded() || isOneTrustPresent();
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [resolved, setResolved] = useState<Resolved | null>(evaluate);

  useEffect(() => {
    if (resolved) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setHasTimedOut(true);
    }, contactRoutingConfig.flag.eligibilityTimeoutMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [resolved]);

  useEffect(() => {
    if (resolved) {
      return;
    }
    const next = evaluate();
    if (next) {
      setResolved(next);
    } else if (hasTimedOut) {
      setResolved(UNKNOWN);
    }
  }, [resolved, hasOneTrustLoaded, hasTimedOut]);

  return resolved ?? PENDING;
};
